import React from 'react';
import { t } from 'ttag';
import { useApolloClient, useMutation } from '@apollo/react-hooks';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Loading from '../Loading';
import { CONTRIBUTION_TYPE } from '../../utils/enums';
import useNotifier from '../Messages/hooks';
import FormField from '../FormField';
import Form from '../Form';
import { formatDate, formatYear, nexYear } from '../../utils/helpers';
import useApi, { MyClubCApi } from '../../api';
import { extractDataError } from '../../utils/errors';

const initBulkPostData = (data) => ({
  userData: Object.entries(data.userData).map(([k, v]) => ({
    athleteId: k,
    groupId: v,
  })),
  userIds: data.userIds || [],
  addedAt: (data.addedAt && formatDate(data.addedAt)) || '',
  year: (data.year && formatYear(data.year)) || '',
  amount: data.amount || '0',
  type: CONTRIBUTION_TYPE.athlete,
  trainerId: data.trainerId,
});

const initPostData = (data) => ({
  userId: data.userId || null,
  addedAt: (data.addedAt && formatDate(data.addedAt)) || '',
  year: (data.year && formatYear(data.year)) || '',
  amount: data.amount || '0',
  type: CONTRIBUTION_TYPE.athlete,
  trainerId: data.trainerId,
  groupId: data.userData[Number(data.userId)],
});

const AddContributionDialog = ({
  trainerId,
  user,
  contribution,
  open,
  toggle,
  onSubmit,
  userIds,
}) => {
  const pushMessage = useNotifier();
  const [loading, setLoading] = React.useState(false);
  const [selectedAvailableGroups, setSelectedAvailableGroups] = React.useState(
    {}
  );
  const [athletesWithoutGroups, setAthleteWithoutGroups] = React.useState([]);
  const [state, setState] = React.useState({
    errors: [],
  });
  const [availableGroups, setAvailableGroups] = React.useState([]);
  const api = useApi();
  const client = useApolloClient();
  const newApi = new MyClubCApi(client);

  React.useEffect(() => {
    setLoading(true);

    async function fetchDataMu() {
      const fetchUIds = userIds && userIds.length > 0 ? userIds : [user.id];
      const userGyms = await newApi.getContributionAvailableGyms(fetchUIds);
      const athleteWithoutAvailableGroups = userGyms.filter(
        (r) => r.groups.length === 1
      );
      const athleteWithOneAvailableGroups = userGyms.filter(
        (r) => r.groups.length === 1
      );
      const athleteWithManyAvailableGroups = userGyms.filter(
        (r) => r.groups.length > 1
      );
      const defaultSelectedAvailableGroups =
        athleteWithOneAvailableGroups.reduce((map, obj) => {
          map[obj.athleteId] = obj.groups[0].id;
          return map;
        }, {});
      setSelectedAvailableGroups({
        ...selectedAvailableGroups,
        ...defaultSelectedAvailableGroups,
      });
      setAvailableGroups(athleteWithManyAvailableGroups);
      setAthleteWithoutGroups(athleteWithoutAvailableGroups);
    }

    fetchDataMu();
    athletesWithoutGroups.forEach((r) => {
      pushMessage({
        text: t`Виникла помилка: у атлета ${r.athlete.fullName} немає группи`,
      });
    });
    setLoading(false);
  }, []);

  const [createContribution] = useMutation(
    api.schema.contributions.create()[0],
    {
      onCompleted: (d) => {
        const res = d[api.schema.contributions.create()[1]];
        if (res.ok) {
          pushMessage({ text: t`Внесок успішно добавлено` });
          onSubmit && onSubmit(res);
        } else {
          setState({ ...state, errors: res.errors });
          const dataError = extractDataError(res.errors);
          pushMessage({ text: dataError || t`Виникла помилка` });
        }
      },
    }
  );

  const [createBulkContribution] = useMutation(
    api.schema.contributions.createBulk()[0],
    {
      onCompleted: (d) => {
        const res = d[api.schema.contributions.createBulk()[1]];
        if (res.ok) {
          pushMessage({ text: t`Внески успішно добавлено` });
          onSubmit && onSubmit(res);
        } else {
          setState({ ...state, errors: res.errors });
          const dataError = extractDataError(res.errors);
          pushMessage({ text: dataError || t`Виникла помилка` });
        }
      },
    }
  );

  const [updateContribution] = useMutation(
    api.schema.contributions.update()[0],
    {
      variables: { id: contribution && contribution.id },
      onCompleted: (d) => {
        const res = d[api.schema.contributions.update()[1]];
        if (res.ok) {
          pushMessage({ text: t`Внесок успішно обновлено` });
          onSubmit && onSubmit(res);
        } else {
          setState({ ...state, errors: res.errors });
          const dataError = extractDataError(res.errors);
          pushMessage({ text: dataError });
        }
      },
    }
  );

  const handleOnSubmit = (formData) => {
    if (contribution) {
      updateContribution({
        variables: {
          inputData: initPostData({
            ...formData,
            userId: user.id,
            trainerId,
            userData: selectedAvailableGroups,
          }),
        },
      });
    } else if (userIds.length > 0) {
      createBulkContribution({
        variables: {
          inputData: initBulkPostData({
            ...formData,
            userIds,
            trainerId,
            userData: selectedAvailableGroups,
          }),
        },
      });
    } else {
      createContribution({
        variables: {
          inputData: initPostData({
            ...formData,
            userId: user.id,
            trainerId,
            userData: selectedAvailableGroups,
          }),
        },
      });
    }
  };

  if (loading) {
    return <Loading />;
  }
  return (
    <Dialog open={open} onClose={() => toggle(false)}>
      <Form
        onSubmit={handleOnSubmit}
        initial={{
          addedAt: contribution && contribution.addedAt,
          year: contribution && contribution.year,
          amount: contribution && contribution.amount,
        }}
        errors={state.errors}
      >
        {({ data, change, errors, submit }) => (
          <>
            <DialogTitle>
              {user ? t`Внесок: ${user.fullName}` : t`Внесок:`}
            </DialogTitle>
            <DialogContent>
              <FormField
                required
                fullWidth
                type='date'
                margin='normal'
                label={t`Дата внеску`}
                id='addedAt'
                name='addedAt'
                variant='outlined'
                value={data.addedAt}
                errors={errors.added_at}
                onChange={change}
              />
              <FormField
                required
                fullWidth
                views={['year']}
                type='date'
                margin='normal'
                label={t`Рік внеску`}
                id='year'
                name='year'
                variant='outlined'
                value={data.year}
                errors={errors.year}
                onChange={change}
                maxDate={nexYear()}
              />
              <FormField
                required
                fullWidth
                type='number_field'
                margin='normal'
                label={t`Сума внеску`}
                id='amount'
                name='amount'
                variant='outlined'
                value={data.amount}
                errors={errors.amount}
                onChange={change}
              />
              {!contribution &&
                availableGroups.map((r) => (
                  <div key={r.id}>
                    <span>{`Виберіть групу для ${r.athlete.fullName}`}</span>
                    <FormField
                      required
                      fullWidth
                      type='select'
                      margin='normal'
                      label={t`Група`}
                      id='groupId'
                      name='groupId'
                      labelKey='name'
                      getLabel={(o) =>
                        `${o.gym.name}/${o.name}/${o.sectionObj.name}`
                      }
                      variant='outlined'
                      options={r.groups}
                      value={selectedAvailableGroups[r.athleteId]}
                      errors={errors.group_id}
                      onChange={(e) => {
                        setSelectedAvailableGroups({
                          ...selectedAvailableGroups,
                          [r.athleteId]: e.target.value,
                        });
                      }}
                    />
                  </div>
                ))}
            </DialogContent>
            <DialogActions>
              <Button
                style={{ margin: '20px 0 20px 0' }}
                margin='normal'
                type='button'
                variant='contained'
                color='primary'
                onClick={submit}
              >
                {t`Зберегти`}
              </Button>

              <Button onClick={() => toggle(false)} color='primary' autoFocus>
                {t`Відмінити`}
              </Button>
            </DialogActions>
          </>
        )}
      </Form>
    </Dialog>
  );
};
export default AddContributionDialog;
